import { SyntheticEvent, useEffect, useMemo } from "react";

import { AutoFixHighRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
  Typography,
  TextField,
  FormControl,
  Skeleton,
  Tooltip,
} from "@mui/material";

import {
  ResizableSidePanel,
  wouldSidePanelTakeOver,
} from "@/common/components";
import {
  describeIncidentNumberFormat,
  getIncidentNumberFormat,
  validateIncidentNumber,
} from "@/common/incidentNumberFormat";
import ShareReportContent from "@/components/online-reporting/report-view/ShareReportContent";

import {
  NotesPanelReportTabs,
  type ReportTabItem,
} from "./NotesPanelReportTabs";
import styles from "./NotesSidePanel.module.scss";
import type {
  NotesSidePanelProps,
  NotesSidebarTab,
} from "./NotesSidePanel.types";
import { SubmissionActivityTab } from "./SubmissionActivityTab";
import { useNotesSidePanel } from "./useNotesSidePanel";

// Min resize width (px) of the notes panel. Shared with the parent's width
// clamps so they don't drift above the panel's own floor.
export const NOTES_PANEL_MIN_WIDTH = 270;

// Width the report area beside the panel must keep. Reserving the report's
// floor means the panel shrinks before the report does.
export const NOTES_PANEL_MIN_REPORT_WIDTH = 640;

// Would opening the notes panel right now cover the report entirely (fullscreen
// takeover) instead of sitting beside it? Parents use this to decide whether
// the panel should be open on mount.
export const wouldNotesPanelTakeOver = (): boolean =>
  wouldSidePanelTakeOver(NOTES_PANEL_MIN_WIDTH, NOTES_PANEL_MIN_REPORT_WIDTH);

const NOTES_SIDEBAR_TABS: { value: NotesSidebarTab; label: string }[] = [
  { value: "notes", label: "Notes" },
  { value: "sharing", label: "Sharing" },
  { value: "activity", label: "Activity" },
];

const NotesSidePanel: React.FC<NotesSidePanelProps> = (props) => {
  const {
    isOpen,
    isClosed,
    readOnly = false,
    requireSynopsisVerification = false,
    synopsisVerified = false,
    serviceConfig,
    appName,
    orgId,
    activeSidebarTab,
    onActiveSidebarTabChange,
    revertIncidentNumberRef,
  } = props;

  const isDisabled = isClosed || readOnly;
  const handleTabChange = (_event: SyntheticEvent, tab: NotesSidebarTab) => {
    onActiveSidebarTabChange(tab);
  };

  const {
    panelWidth,
    setPanelWidth,
    allReports,
    currentReportIndex,
    isCurrentReportExported,
    isMainReportExported,
    currentNotes,
    currentSynopsis,
    currentIncidentNumber,
    currentModified,
    currentNotesModified,
    setCurrentNotes,
    setCurrentSynopsis,
    setCurrentIncidentNumber,
    handleClose,
    navigateToReport,
    generateSynopsis,
    handleSave,
    isSaving,
    isGeneratingSynopsis,
    synopsisVerifiedLoading,
    enforceSynopsisVerification,
    reportMetadata,
    onWidthChange,
    handleSynopsisVerificationChange,
  } = useNotesSidePanel(props);

  /* Agency-defined incident number shape. The field is controlled, so the error
     derives straight from the current value — the parent's guard blocks the save. */
  const incidentNumberFormat = getIncidentNumberFormat(serviceConfig?.metadata);
  const incidentNumberError = validateIncidentNumber(
    currentIncidentNumber,
    incidentNumberFormat,
  );

  const incidentNumberHelperText = () => {
    if (incidentNumberError) return incidentNumberError;

    const autoGenerated =
      serviceConfig?.metadata?.automated_incident_number_generation;

    const example = incidentNumberFormat
      ? describeIncidentNumberFormat(incidentNumberFormat)
      : null;

    if (example) {
      const format = `Format: ${example}`;
      return autoGenerated
        ? `${format} — auto-generated if left blank`
        : format;
    }

    return autoGenerated ? "Auto-generated if left blank" : "";
  };

  useEffect(() => {
    if (!revertIncidentNumberRef) return;
    revertIncidentNumberRef.current = () =>
      setCurrentIncidentNumber(reportMetadata?.incident_number ?? "");
    return () => {
      revertIncidentNumberRef.current = null;
    };
  });

  const exportedTooltip = "Exported records cannot be modified";

  const activitySubmissionIds = useMemo(
    () =>
      allReports
        .map((report) => report?.submission_id ?? "")
        .filter((id) => id.length > 0),
    [allReports],
  );

  const supplementalLabels = useMemo(
    () =>
      Object.fromEntries(
        allReports
          .filter(
            (report) => !!report?.is_supplemental && !!report?.submission_id,
          )
          .map((report, index) => [
            report.submission_id as string,
            `Supplemental Report ${index + 1}`,
          ]),
      ),
    [allReports],
  );

  const headerTabs = (
    <Box className={styles.topTabsContainer}>
      <Tabs
        value={activeSidebarTab}
        onChange={handleTabChange}
        className={styles.topTabs}
        aria-label="Side panel sections"
        // Scroll instead of overflowing into the close button when narrow.
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {NOTES_SIDEBAR_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
    </Box>
  );

  if (!isOpen) return null;

  return (
    <ResizableSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      width={panelWidth}
      onWidthChange={(w) => {
        setPanelWidth(w);
        onWidthChange?.(w);
      }}
      minWidth={NOTES_PANEL_MIN_WIDTH}
      minSiblingWidth={NOTES_PANEL_MIN_REPORT_WIDTH}
      onTakeoverChange={props.onTakeoverChange}
      title={headerTabs}
      data-testid="notes-side-panel"
      side="right"
      ariaLabel="Police record, sharing, and activity"
    >
      <Box className={styles.formContainer}>
        {activeSidebarTab === "notes" && (
          <>
            <Box className={styles.fieldSection}>
              <Typography variant="body2" className={styles.fieldLabel}>
                RMS Incident / Occurrence Number
              </Typography>
              <Tooltip
                title={isMainReportExported ? exportedTooltip : ""}
                arrow
              >
                <FormControl fullWidth>
                  <TextField
                    id="incidentNumber"
                    value={currentIncidentNumber}
                    disabled={isDisabled || isMainReportExported}
                    variant="outlined"
                    placeholder={
                      serviceConfig?.metadata
                        ?.automated_incident_number_generation
                        ? "Auto-generated if left blank"
                        : "No RMS Incident / occurrence number yet."
                    }
                    onChange={(e) => setCurrentIncidentNumber(e.target.value)}
                    error={Boolean(incidentNumberError)}
                    helperText={incidentNumberHelperText()}
                    className={styles.incidentNumberField}
                  />
                </FormControl>
              </Tooltip>
            </Box>

            <NotesPanelReportTabs
              reports={allReports as ReportTabItem[]}
              currentIndex={currentReportIndex}
              onNavigate={navigateToReport}
            />

            <Box className={styles.fieldSection}>
              <Typography variant="body2" className={styles.fieldLabel}>
                Internal Private Notes
              </Typography>
              <Tooltip
                title={isCurrentReportExported ? exportedTooltip : ""}
                arrow
              >
                <FormControl fullWidth>
                  <TextField
                    id="additionalNotes"
                    value={currentNotes}
                    disabled={isDisabled || isCurrentReportExported}
                    variant="outlined"
                    multiline
                    minRows={4}
                    placeholder="Internal notes here."
                    onChange={(e) => setCurrentNotes(e.target.value)}
                    className={currentNotesModified ? styles.modified : ""}
                  />
                </FormControl>
              </Tooltip>
            </Box>

            <Box className={styles.synopsisSection}>
              <Box className={styles.synopsisHeader}>
                <Typography variant="body2" className={styles.synopsisTitle}>
                  Synopsis
                </Typography>
                {!isDisabled && !isCurrentReportExported && (
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={generateSynopsis}
                    loading={isGeneratingSynopsis}
                    disabled={isDisabled}
                    startIcon={<AutoFixHighRounded />}
                    className={styles.generateButton}
                  >
                    Generate
                  </LoadingButton>
                )}
              </Box>
              {!isDisabled && (
                <Typography
                  variant="body2"
                  className={styles.synopsisHelperText}
                >
                  For the best synopsis, please correct any errors and add
                  missing details to the report first. The synopsis will be
                  included in the RMS export but won&apos;t be shared with the
                  reporting individual.
                  {requireSynopsisVerification &&
                    " You must verify the accuracy of the information before proceeding."}
                </Typography>
              )}
              {isGeneratingSynopsis ? (
                <Box className={styles.skeletonContainer}>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                </Box>
              ) : (
                <Tooltip
                  title={isCurrentReportExported ? exportedTooltip : ""}
                  arrow
                >
                  <FormControl fullWidth>
                    <TextField
                      id="synopsis"
                      value={currentSynopsis}
                      disabled={isDisabled || isCurrentReportExported}
                      variant="outlined"
                      multiline
                      minRows={5}
                      placeholder="No synopsis generated yet."
                      onChange={(e) => setCurrentSynopsis(e.target.value)}
                    />
                  </FormControl>
                </Tooltip>
              )}
              {!isDisabled && enforceSynopsisVerification && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={synopsisVerified}
                      disabled={synopsisVerifiedLoading}
                      onChange={(e) =>
                        handleSynopsisVerificationChange(e.target.checked)
                      }
                    />
                  }
                  label="I confirm that I have verified the details of this synopsis"
                  className={styles.synopsisVerificationCheckbox}
                />
              )}
            </Box>

            {!isDisabled && (
              <LoadingButton
                variant="contained"
                color="info"
                size="large"
                loading={isSaving}
                fullWidth
                onClick={handleSave}
                disabled={!currentModified || Boolean(incidentNumberError)}
                className={styles.saveButton}
              >
                Save Changes
              </LoadingButton>
            )}
          </>
        )}

        {activeSidebarTab === "sharing" && (
          <Box className={styles.sharingSection}>
            <ShareReportContent
              appName={appName}
              orgId={orgId}
              submissionId={reportMetadata?.submission_id ?? ""}
            />
          </Box>
        )}

        {activeSidebarTab === "activity" && (
          <SubmissionActivityTab
            submissionIds={activitySubmissionIds}
            supplementalLabels={supplementalLabels}
          />
        )}
      </Box>
    </ResizableSidePanel>
  );
};

export default NotesSidePanel;
